import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import soundFile from "./sound.mp3";
import soundFileBid from "./sound-bid.mp3";
import { useHubEvent } from "../../hooks/useHubEvent";
import { useHubResync } from "../../hooks/useHubResync";
import { useLotCountdown } from "../../hooks/useLotCountdown";
import {
  RECHECK_AFTER_ZERO_MS,
  isClosedStatus,
  isOlder,
  nextClock,
  withoutClockFields,
} from "../../utils/helper/lotClock";
import { isSubscriptionRefusal, mergeFigures } from "../../utils/helper/lotPage";

// #191: after the recheck at the deadline + RECHECK_AFTER_ZERO_MS, how often - and how many more
// times - the page asks again while no close has arrived.
const RECHECK_RETRY_MS = 2000;
const RECHECK_RETRIES = 3;
/** How long the "Bidding Ended" dialog shows before the page returns to its list. */
const END_NAVIGATE_DELAY_MS = 2000;
/** FE-14 QA (rows 1658/1236): how long "Paused" shows before the page returns to its list. */
const PAUSE_NAVIGATE_DELAY_MS = 2000;

/** Plays a sound and stops it after `ms`. */
const playFor = (file, ms) => {
  const audio = new Audio(file);
  audio.play().catch((error) => {
    console.error("Error playing sound:", error);
  });
  setTimeout(() => {
    audio.pause();
    audio.currentTime = 0;
  }, ms);
};

/**
 * One lot's page: its figures, its clock and its close, for SecodaryPlaceBid (a resale listing).
 * PlaceBid (a team in a primary auction) shared it until Auction Details replaced that page (#237);
 * the hook still takes either kind of lot (FE-16).
 *
 * #191: everything comes from what the server sent for this lot - its REST reload and its two hub
 * events - through applyLot, and nothing else: not the router state (a reload replays it), not
 * localStorage, not the device clock. The pages used to keep minutes and seconds of their own,
 * restarted by any push - a late one rewound them - and ticked on through a pause. 00:00 closes
 * nothing on the server: bidding is disabled and the server is asked.
 *
 * FE-14 QA (rows 1647/1648/1245): "Bidding Ended", the closing sound and the return to the list are
 * timed from the screen's 00:00, not from the server's close - which arrives 1-4 s later (the
 * Worker's close push and its cycle). That end is provisional: a row with a new deadline (the lot
 * restarted) or a pause takes it back; the server's close, when it comes, only settles won/lost.
 * FE-14 QA (rows 1658/1236): a Paused lot returns the player to the list after
 * PAUSE_NAVIGATE_DELAY_MS, as before the refactor; a Live row before then keeps the page open.
 *
 * @param {object} options
 * @param {object|null} options.lot The router state: the row the page was opened from. Its figures
 *   are shown until the server's arrive; never its clock.
 * @param {(row: object) => boolean} options.isThisRow Whether a pushed row is this lot.
 * @param {(row: object) => object} options.pickFigures The figures to take from a row.
 * @param {() => Promise<{row: object}|{gone: true}|null>} options.load Reloads the lot
 *   (readListingReply of the page's request).
 * @param {[string, string]} options.pushEvents The bid status and the new bid hub events.
 * @param {string[]} [options.closedCodes] Refusal errorCodes after which the lot takes no bid again.
 * @param {string} options.exitTo Where the page returns to once the lot has ended.
 * @returns {object} What the page shows, and submitBid.
 */
export const useLotPage = ({
  lot: seed,
  isThisRow,
  pickFigures,
  load,
  pushEvents: [statusEvent, bidEvent],
  closedCodes = [],
  exitTo,
}) => {
  const navigate = useNavigate();
  const isUser = JSON.parse(localStorage.getItem("isUser"));
  const userId = isUser?.userId;

  // Read when they run, from timers and hub handlers set up on an earlier render.
  const optionsRef = useRef(null);
  optionsRef.current = { isThisRow, pickFigures, load, closedCodes, exitTo };

  // The figures shown - price, bids, pool, leader. Seeded from the row the player clicked (never
  // its clock, #191) until the server's own figures arrive.
  const [figures, setFigures] = useState(() => withoutClockFields(seed) ?? null);
  const figuresRef = useRef(figures);
  figuresRef.current = figures;
  // The lot as the server's last REST reply described it.
  const [loadedRow, setLoadedRow] = useState(null);

  const [lot, setLot] = useState(null);
  const lotRef = useRef(null);
  // The last deadline seen while the lot was Live: what the recheck after 00:00 is timed from.
  const deadlineRef = useRef({ endsAt: null, endsAtUtc: null });
  // A refusal said the lot takes no more bids; it stays disabled whatever arrives next.
  const [biddingClosed, setBiddingClosed] = useState(false);
  // The lot has ended for good: the "Bidding Ended" dialog is showing.
  const [ended, setEnded] = useState(false);
  const [wonLot, setWonLot] = useState(false);
  // A bid is on its way, or it was accepted and the server's next row for the lot has not arrived.
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // An accepted bid waits for that row - from the reload or a push, whichever comes first.
  const awaitingRowRef = useRef(false);
  const endedRef = useRef(false);
  const activeRef = useRef(true);
  const endNavigateRef = useRef(null);
  // FE-14: "Bidding Ended" shown at the screen's 00:00, before the server's close has arrived.
  const provisionalEndRef = useRef(false);
  const pauseNavigateRef = useRef(null);
  const recheckRef = useRef({ deadline: null, timeoutId: null });
  const previousSecondsRef = useRef(null);
  const warnedForRef = useRef(null);

  const { seconds, text: countdownText } = useLotCountdown(
    lot?.status === "Live" ? lot.endsAt : null,
    lot?.status === "Paused" ? lot.frozenSeconds : null
  );

  const cancelRecheck = () => {
    clearTimeout(recheckRef.current.timeoutId);
    recheckRef.current = { deadline: null, timeoutId: null };
  };

  const cancelPauseExit = () => {
    clearTimeout(pauseNavigateRef.current);
    pauseNavigateRef.current = null;
  };

  /** FE-14: the lot is Paused - back to the list shortly, unless it goes Live again first. */
  const schedulePauseExit = () => {
    if (pauseNavigateRef.current !== null) return;
    pauseNavigateRef.current = setTimeout(() => {
      pauseNavigateRef.current = null;
      if (activeRef.current) navigate(optionsRef.current.exitTo);
    }, PAUSE_NAVIGATE_DELAY_MS);
  };

  /** The end dialog: won or lost, the end sound, then back to the list. */
  const showEnd = (winner) => {
    setWonLot(winner != null && Number(winner) === Number(userId));
    setEnded(true);
    playFor(soundFile, 3000);
    clearTimeout(endNavigateRef.current);
    endNavigateRef.current = setTimeout(
      () => navigate(optionsRef.current.exitTo),
      END_NAVIGATE_DELAY_MS
    );
  };

  /**
   * FE-14: the countdown on screen reached 00:00 - end the page now, from the figures shown. The
   * recheck keeps asking the server, whose close settles won/lost.
   */
  const endAtZero = () => {
    if (endedRef.current || provisionalEndRef.current || !activeRef.current) return;
    provisionalEndRef.current = true;
    showEnd(figuresRef.current?.bidPlacedBy);
  };

  /** FE-14: the server says the lot goes on (a new deadline, or a pause) - take the end back. */
  const undoEndAtZero = () => {
    if (!provisionalEndRef.current) return;
    provisionalEndRef.current = false;
    clearTimeout(endNavigateRef.current);
    endNavigateRef.current = null;
    setEnded(false);
  };

  /** The page takes a bid again. */
  const releaseBid = () => {
    awaitingRowRef.current = false;
    submittingRef.current = false;
    setSubmitting(false);
  };

  /**
   * The lot is over: the dialog with the result, the end sound, then back to the list. #191 reopen:
   * for any Closed row. A Closed row inside the second after the deadline used to wait, because a bid
   * in that second restarted the lot; the server takes none there any more.
   *
   * @param {object|null} row The row that closed it; null for a reply with no lot at all.
   */
  const endLot = (row) => {
    if (endedRef.current || !activeRef.current) return;
    endedRef.current = true;
    cancelRecheck();
    cancelPauseExit();

    // Won or lost is the closing row's leader. It used to be guessed from the bidders of every
    // bid push, comma-joined into localStorage.
    const winner = row?.bidPlacedBy !== undefined ? row.bidPlacedBy : figuresRef.current?.bidPlacedBy;
    // FE-14: already ended at 00:00 - the dialog, the sound and the return are under way.
    if (provisionalEndRef.current) {
      provisionalEndRef.current = false;
      setWonLot(winner != null && Number(winner) === Number(userId));
      return;
    }
    showEnd(winner);
  };

  /**
   * Applies one row the server sent for this lot: its figures, and its clock through nextClock.
   * Decided against refs, not state - several hub messages can arrive in one tick.
   *
   * @param {object} row A lot row (REST or hub).
   * @param {number} receivedAt Date.now() when it arrived.
   */
  const applyLot = (row, receivedAt) => {
    if (!row || endedRef.current || !activeRef.current) return;
    const prev = lotRef.current;
    // Newest wins: a row produced before the last one applied is out of date.
    if (prev && isOlder(row, prev.serverNow)) return;

    const final = isClosedStatus(row.status);
    const next = nextClock(prev, row, receivedAt);
    lotRef.current = next;
    setLot(next);
    const picked = optionsRef.current.pickFigures(row);
    setFigures((shown) => mergeFigures(shown, picked));

    if (next.status === "Live" && Number.isFinite(next.endsAt)) {
      const known = deadlineRef.current;
      // A new deadline - a bid restarted the clock, or the lot resumed - cancels a recheck, and an
      // end shown at 00:00 (FE-14).
      if ((next.endsAtUtc ?? next.endsAt) !== (known.endsAtUtc ?? known.endsAt)) {
        cancelRecheck();
        if (next.endsAt > receivedAt) undoEndAtZero();
      }
      deadlineRef.current = { endsAt: next.endsAt, endsAtUtc: next.endsAtUtc };
    }

    // FE-14: a pause sends the player back to the list; going Live again first keeps the page.
    if (next.status === "Paused") {
      undoEndAtZero();
      schedulePauseExit();
    } else if (!final) {
      cancelPauseExit();
    }

    // The first row after an accepted bid shows who leads now. A push can bring it before the
    // reload does - the reload can take up to ~90 s on a slow server - and a player it shows
    // outbid must be able to bid back. An older row returned above, so it releases nothing.
    if (awaitingRowRef.current) releaseBid();

    if (final) endLot(row);
  };

  /**
   * Reloads the lot: on mount, after a bid or a refusal, at a recheck, when the tab becomes visible
   * and after a live-feed reconnect (#182, #183). #183: the figures on screen used to come only from
   * the router's history state, and a browser reload keeps that state, so a missed push stayed
   * missed even after F5.
   */
  const reload = async () => {
    let reply;
    try {
      reply = await optionsRef.current.load();
    } catch (error) {
      console.error("Error fetching the lot:", error);
      return;
    }
    const receivedAt = Date.now();
    // No reply at all: apiClient has already said so, and the lot is as it was.
    if (!reply || !activeRef.current) return;

    // #191: no lot in the reply (204) is a final close.
    if (reply.gone) {
      endLot(null);
      return;
    }

    setLoadedRow(reply.row);
    applyLot(reply.row, receivedAt);
  };

  /**
   * Asks the server about a lot whose countdown reached 00:00 without a close: at the deadline +
   * RECHECK_AFTER_ZERO_MS (just after the Worker's close push is due), then every RECHECK_RETRY_MS,
   * at most RECHECK_RETRIES more times. A close, a new deadline or leaving the page cancels it.
   */
  const scheduleRecheck = () => {
    const deadline = deadlineRef.current.endsAt;
    const key = deadlineRef.current.endsAtUtc ?? deadline ?? "none";
    if (endedRef.current || recheckRef.current.deadline === key) return;
    cancelRecheck();

    const run = (attempt, delay) => {
      recheckRef.current = {
        deadline: key,
        timeoutId: setTimeout(async () => {
          await reload();
          if (endedRef.current || recheckRef.current.deadline !== key || attempt >= RECHECK_RETRIES) return;
          run(attempt + 1, RECHECK_RETRY_MS);
        }, delay),
      };
    };
    const firstAt = (Number.isFinite(deadline) ? deadline : Date.now()) + RECHECK_AFTER_ZERO_MS;
    run(0, Math.max(0, firstAt - Date.now()));
  };

  // 00:00 on this screen closes nothing on the server: bidding is disabled (see canBid) and the
  // server is asked. FE-14: the page shows the end at once (endAtZero). A Closed row needs no
  // recheck any more (#191 reopen): it ends the page as it arrives.
  useEffect(() => {
    if (!lot || endedRef.current) return;
    if (lot.status === "Live" && Number.isFinite(lot.endsAt) && seconds === 0) {
      scheduleRecheck();
      endAtZero();
    }
    // scheduleRecheck works from refs, so its new copy on each render is no reason to run again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lot, seconds, ended]);

  // The 3-second warning, once per deadline, when the countdown crosses into its last 3 seconds.
  // It used to play on an exact 00:03, which a skipped or repeated second missed or played twice.
  useEffect(() => {
    const previous = previousSecondsRef.current;
    previousSecondsRef.current = seconds;
    const current = lotRef.current;
    if (current?.status !== "Live" || !(previous > 3) || seconds < 1 || seconds > 3) return;

    const deadlineKey = current.endsAtUtc ?? current.endsAt;
    if (warnedForRef.current === deadlineKey) return;
    warnedForRef.current = deadlineKey;
    playFor(soundFileBid, 3000);
  }, [seconds]);

  // Mount: load the lot, and reload it quietly whenever the tab shows again - a hidden tab may have
  // missed pushes.
  useEffect(() => {
    reload();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") reload();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const active = activeRef;
    const recheck = recheckRef;
    const endNavigate = endNavigateRef;
    const pauseNavigate = pauseNavigateRef;
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      active.current = false;
      clearTimeout(recheck.current.timeoutId);
      clearTimeout(endNavigate.current);
      clearTimeout(pauseNavigate.current);
    };
    // Once per visit: reload reads this visit's options and refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every row for this lot - pushes may carry it more than once, or not at all.
  const applyPushed = (data) => {
    if (!Array.isArray(data)) return;
    const receivedAt = Date.now();
    data.filter(optionsRef.current.isThisRow).forEach((row) => applyLot(row, receivedAt));
  };

  // The Worker's push of every unfinished lot (and of a lot it has just closed), and the
  // play/pause broadcast - which is how a pause reaches this page.
  useHubEvent(statusEvent, applyPushed);
  // Sent when someone bids; a bid restarts the lot's clock.
  useHubEvent(bidEvent, applyPushed);
  // #182/#183: pushes sent while the live connection was down are lost; reload once it is back.
  useHubResync(() => reload());

  /**
   * Sends a bid. While it is on its way - and, once accepted, until the server's next row for the
   * lot arrives, from the reload or a push - the page takes no other bid, so a double click sends
   * one. Until that row the page still shows the old leader, so the button would be live, and the
   * server does take a bid from a player who already leads.
   *
   * @param {(body: Array) => Promise<object>} send The bid request (BidReg or SmBidReg).
   * @param {Array} body Its body.
   * @returns {Promise<boolean>} Whether the bid was accepted.
   */
  const submitBid = async (send, body) => {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    setSubmitting(true);

    const res = await send(body);
    // #183: an id per outcome and wording, so the same answer is never shown twice at once.
    const toastId = `lot-bid-${res?.data?.status === true ? "ok" : "refused"}-${res?.data?.message}`;

    // Both bid endpoints answer with a JSON boolean (it used to be compared with ==).
    if (res?.data?.status === true) {
      toast.success(res?.data?.message, { toastId });
      // #191: the restarted clock and the new leader are the server's - this reload and the bid
      // push both carry them. It used to be restarted here from the device clock.
      awaitingRowRef.current = true;
      await reload();
      // A reload that brought no row - no reply, a failure - must not keep the page locked.
      releaseBid();
      return true;
    }

    releaseBid();
    toast.error(res?.data?.message, { toastId });

    // #190/#191: the lot takes no more bids - closed, completed, or the bidding window before the
    // contest has shut. The button stays disabled whatever arrives next.
    if (optionsRef.current.closedCodes.includes(res?.data?.errorCode)) {
      setBiddingClosed(true);
    }

    // The server checks a bid against the lot's real figures (#145), so a refusal usually means
    // this screen is out of date - the minimum next bid, or the lot's state (AUCTION_PAUSED,
    // BIDDING_NOT_STARTED, a close; a resale refusal carries no errorCode to say which). Reload the
    // lot rather than wait for the next hub message.
    reload();

    // Send an unsubscribed or lapsed player to the payment page. Account Settings stays the
    // destination even though single auctions can now be bought one at a time: this page is the
    // resale market's, and a single-auction purchase is only offered while bidding on the auction's
    // own teams is open (#289), which a resale usually outlasts. That offer lives on the Auctions
    // list and Auction Details instead.
    if (isSubscriptionRefusal(res?.data?.message)) {
      navigate("/Player/Account_settings");
    }
    return false;
  };

  const isHighBidder =
    figures?.bidPlacedBy != null && Number(figures.bidPlacedBy) === Number(userId);
  // #191: only while the server says Live and the countdown has time left - never decided by the
  // device clock running past the deadline into a lot the server has not closed yet.
  const canBid =
    !submitting && lot?.status === "Live" && seconds > 0 && !biddingClosed && !isHighBidder && !ended;

  return {
    lot,
    figures,
    loadedRow,
    seconds,
    countdownText,
    ended,
    wonLot,
    biddingClosed,
    submitting,
    isHighBidder,
    canBid,
    submitBid,
  };
};
